import React, { useState } from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles'
import clsx from 'clsx'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTranslation } from 'gatsby-plugin-react-i18next'

import Link from '@talentinc/gatsby-theme-ecom/components/Link'
import { NavigationGroup } from '@talentinc/gatsby-theme-ecom/types/page'
import NavigationGroupLink from '@talentinc/gatsby-theme-ecom/components/NavigationGroup/NavigationGroupLink'
import NavigationGroupDropdown from '@talentinc/gatsby-theme-ecom/components/NavigationGroup/NavigationGroupDropdown'
import { NavToolbar, NavLinkContainer } from './Header'
import useUrlPathBuilder from '@talentinc/gatsby-theme-ecom/hooks/useUrlPathBuilder'
import MiniSearchForm from '@talentinc/gatsby-theme-ecom/components/Search/MiniSearchForm'

interface QueryResults {
  mainCategories: NavigationGroup
  dropdownCategories: NavigationGroup
}

interface Props {
  className?: string
}

const BlogNavigation: React.FC<Props> = ({ className }) => {
  const classes = useStyles()
  const [searchFocused, setSearchFocused] = useState(false)
  const { urlPathBuilder, blogPrefix } = useUrlPathBuilder()
  const { t } = useTranslation()
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'))
  const { mainCategories, dropdownCategories } =
    useStaticQuery<QueryResults>(graphql`
      query BlogCategoryNavigation {
        mainCategories: contentfulNavigationGroup(
          contentful_id: { eq: "26gj8IsE9LFw1TW2xrd5Qd" }
        ) {
          ...NavigationGroup
        }
        dropdownCategories: contentfulNavigationGroup(
          contentful_id: { eq: "3LSgzeFd0sj2Ny7cENz1RT" }
        ) {
          ...NavigationGroup
        }
      }
    `)

  return (
    <NavToolbar
      // @ts-ignore
      component="nav"
      className={clsx(classes.root, className)}
    >
      <Link to={urlPathBuilder([blogPrefix])} className={classes.ourBlog}>
        {t('navigation.ourBlog')}
      </Link>
      <NavLinkContainer
        data-contentful-id={mainCategories.contentful_id}
        className={classes.links}
      >
        <>
          {mainCategories.links.map((link) => (
            <NavigationGroupLink key={link.contentful_id} link={link} />
          ))}
          {isMobile &&
            dropdownCategories.links.map((link) => (
              <NavigationGroupLink key={link.contentful_id} link={link} />
            ))}
        </>
      </NavLinkContainer>
      {!isMobile && (
        <>
          {!searchFocused && (
            <NavigationGroupDropdown
              launchButtonProps={{
                children: t('navigation.more'),
              }}
              links={dropdownCategories.links}
            />
          )}
          <MiniSearchForm
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            onClose={() => setSearchFocused(false)}
            classes={{
              root: classes.form,
            }}
          />
        </>
      )}
    </NavToolbar>
  )
}

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      borderTop: `1px solid ${theme.palette.divider}`,
      '&&': {
        [theme.breakpoints.down('sm')]: {
          width: '100%',
          alignItems: 'flex-start',
          padding: '1em 6% .5em 6%',
        },
      },
    },
    form: {
      borderLeft: `1px solid ${
        theme.colors?.gray?.[100] ?? theme.palette.text.hint
      }`,
      marginLeft: '1.43125em',
      height: '2.466875rem',
    },
    ourBlog: {
      fontFamily: theme.typography.h1.fontFamily,
      fontSize: '1.3125em',
      fontWeight: 'bold',
      letterSpacing: '-0.2px',
      lineHeight: 1.4285714286,
      minWidth: '7.6875rem',
    },
    links: {
      '&&': {
        [theme.breakpoints.down('sm')]: {
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          maxWidth: '100%',
          paddingLeft: 0,
          paddingRight: 0,
          marginBottom: 0,

          '&& > a': {
            fontFamily: theme.typography.body1.fontFamily,
            fontSize: '0.9em',
            fontWeight: 'normal',
            width: '50%',
            border: 0,
          },
        },
      },
    },
  })
)

export default BlogNavigation
